import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { NOTIFICATION_TYPES, setMessage } from '../../../toasts';
import { handleAxiosErrorMessage } from '../../../../utils';

import {
  LEAD_MUTATION_KEYS,
  LEAD_QUERY_KEYS,
  LEAD_EVENT_INVALID_RESPONSE,
  STAGE_ALREADY_EXISTS,
} from '../utils/leadApi.constants';
import { mapLeadEvent } from '../mappers';
import { postLeadEvent } from '../services';

export function usePostLeadEvent({
  onSuccess,
  onError,
  invalidateEventsOnSuccess = true,
  mergeEventIntoCacheOnSuccess = true,
  suppressDefaultErrorToast = false,
} = {}) {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const { t } = useTranslation(['system']);

  return useMutation({
    mutationKey: LEAD_MUTATION_KEYS.postEvent,
    mutationFn: async (payload) => {
      const dto = await postLeadEvent(payload);
      if (!dto?.data) {
        throw new Error(LEAD_EVENT_INVALID_RESPONSE);
      }
      return mapLeadEvent(dto.data);
    },
    onSuccess: (event) => {
      if (invalidateEventsOnSuccess) {
        queryClient.invalidateQueries({ queryKey: LEAD_QUERY_KEYS.events });
      } else if (mergeEventIntoCacheOnSuccess) {
        queryClient.setQueryData(LEAD_QUERY_KEYS.events, (old) => {
          const list = Array.isArray(old) ? [...old] : [];
          if (list.some((e) => e.stage === event.stage)) return list;
          return [...list, event];
        });
      }
      onSuccess?.(event);
    },
    onError: (error, variables, context) => {
      const errorCode = handleAxiosErrorMessage(error);
      if (!suppressDefaultErrorToast && errorCode !== STAGE_ALREADY_EXISTS) {
        dispatch(
          setMessage({
            body: t(errorCode, { ns: 'system' }),
            type: NOTIFICATION_TYPES.ERROR,
          })
        );
      }
      onError?.(error, variables, context);
    },
  });
}
